import { supabase } from "@/integrations/supabase/client";

export type BookedSlotRow = { date_time: string };
export type BranchBookedSlotRow = { branch_id: string; date_time: string };

export const bookedSlotsQueryKey = (branchId: string) => ["wa-booking-range", branchId] as const;

/**
 * Taken slots for a branch, via a security-definer RPC.
 *
 * Reading `bookings` directly is blocked by RLS for anonymous visitors, which
 * silently returned an empty list and made every slot look free (leading to
 * `slot_taken` 409s at submit time). The RPC exposes date_time only — no PII.
 */
export async function fetchBookedSlots(branchId: string): Promise<BookedSlotRow[]> {
  const { data, error } = await supabase.rpc("get_branch_booked_slots", {
    _branch_id: branchId,
  });
  if (error) throw error;
  return (data ?? []) as BookedSlotRow[];
}

export const bookedSlotsQueryOptions = (branchId: string) => ({
  queryKey: bookedSlotsQueryKey(branchId),
  queryFn: () => fetchBookedSlots(branchId),
  staleTime: 15_000,
});

/**
 * Taken slots for SEVERAL branches in one round-trip (next 4 days only).
 * Used by the "next available slots" block rendered on each visible branch
 * card, so expanding a city costs one request instead of one per branch.
 */
export async function fetchBranchesBookedSlots(
  branchIds: string[]
): Promise<BranchBookedSlotRow[]> {
  if (branchIds.length === 0) return [];
  const { data, error } = await (supabase as any).rpc("get_branches_booked_slots", {
    _branch_ids: branchIds,
  });
  if (error) throw error;
  return (data ?? []) as BranchBookedSlotRow[];
}

export const branchesBookedSlotsQueryOptions = (branchIds: string[]) => ({
  // Sorted key → stable cache across re-renders.
  queryKey: ["branches-booked-slots", [...branchIds].sort().join(",")] as const,
  queryFn: () => fetchBranchesBookedSlots(branchIds),
  staleTime: 30_000,
  enabled: branchIds.length > 0,
});
